import React from 'react';
import PropTypes from 'prop-types';
import {
    Accordion as MuiAccordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Divider,
    Card
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { ArrowDownLinearIcon } from '../../../../../../icons-lib';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    width: '654px',
    marginBottom: theme.spacing(2),
}));

// Styled Accordion
const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
    "borderRadius": '20px',
    "boxShadow": 'none',
    '&:not(:last-child)': {
        marginBottom: theme.spacing(2)
    }
}));

// Styled Accordion Summary
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    "backgroundColor": '#FFFFFF',
    "borderBottom": '1px solid transparent',
    '&.Mui-expanded': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`
    },
    "color": theme.palette.primary.main,
    '& .MuiAccordionSummary-expandIconWrapper': {
        "borderRadius": '8px',
        '&:hover': {
            backgroundColor: theme.palette.grey[200]
        }
    }
}));

// Styled Accordion Details
const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: 0,
    backgroundColor: '#FFFFFF'
}));

// Styled Icon Slot
const StyledIconSlot = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '8px',
    background: theme.palette.gradients.primary,
    color: '#FFFFFF'
}));

// Base Accordion Component
const CustomAccordion = React.forwardRef(({
    title,
    children,
    expanded,
    onChange,
    icon,
    actions,
}, ref) => {
    return (
        <StyledCard ref={ref}>
            <StyledAccordion expanded={expanded} onChange={onChange}>
                <StyledAccordionSummary expandIcon={<ArrowDownLinearIcon sx={{ transition: 'transform 0.5s ease' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon && <StyledIconSlot>{icon}</StyledIconSlot>}{' '}
                        {/* Slot for the icon */}
                        <Typography sx={{ color: 'inherit' }}>
                            {title}
                        </Typography>
                    </Box>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                    <Box sx={{ padding: '16px 40px' }}>
                        {children}
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: 1
                        }}
                    >
                        {actions.map((action, index) => (
                            <Button
                                key={index}
                                variant="outlined"
                                color="primary"
                                onClick={action.onClick}
                                startIcon={action.icon}
                                sx={{ mr: 1 }}
                            >
                                {action.title}
                            </Button>
                        ))}
                    </Box>
                </StyledAccordionDetails>
            </StyledAccordion>
        </StyledCard>
    );
});

CustomAccordion.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    expanded: PropTypes.bool,
    onChange: PropTypes.func,
    actions: PropTypes.array,
    icon: PropTypes.element
};

export default CustomAccordion;
